import { HorizBox, Pad, PadBox } from "component/basics";
import { Heading, Paragraph, TextField, UtilityText } from "component/text";
import { View } from "react-native";
import { useDatastore, useInstanceKey, usePersonaKey, usePersonaObject, useSiloKey } from "util/datastore";
import { FaceImage, FaceSelect, LetterFace, ProfilePhoto } from "component/people";
import { useEditableField, useEditErrors, WithEditableFields } from "structure/profile";
import { Checkbox, RadioGroup, RadioOption, Toggle } from "component/form";
import { colorTagEmphasized, colorTextSecondary, colorTextOnLightColorTag } from "component/color";
import { Banner } from "component/banner";
import { Catcher } from "system/catcher";
import { useState } from "react";
import { CTAButton } from "component/button";
import { RichText } from "component/richtext";
import { useIsAdmin, useShowCollaborationNote, useSiloName, useLegalDocLinks } from "component/admin";
import { Feature } from "util/stdtypes";
import { ProfileConfig } from "structure/profile";

export const ProfilePhotoAndNameFeature : Feature<ProfileConfig, {}> = {
    key: 'profileeditname',
    name: 'Photo and Name',
    providesConfig: {
        profileModules: [{
            label: 'photo and name', key: 'photoandname', 
            view: ViewPhotoAndName,
            edit: EditPhotoAndName,
            checkForErrors: checkPhotoAndNameAsync,
            makePreview: previewPhotoAndName,
        }]
    }
}

export const ProfileOldStuff : Feature<ProfileConfig, {}> = {
    key: 'profile_old',
    name: 'Old Profile Stuff',
    providesConfig: {
        profileModules: [{
            label: 'photo and name', key: 'old', 
            view: ViewPhotoAndName
        }]
    }
}

function ViewPhotoAndName() {
    const personaKey = useInstanceKey();
    const persona = usePersonaObject(personaKey);

    return <View>    
        <HorizBox center>
            <ProfilePhoto userId={personaKey} type='huge' />
            <Pad />      
            <Heading type="large" weight="medium" text={persona?.name ?? 'Unknown User'} />            
        </HorizBox>
    </View>
}

function EditPhotoAndName() {
    const [nameMode, setNameMode] = useEditableField('nameMode', 'full');
    const datastore = useDatastore();
    const fbUser = datastore.getFirebaseUser();
    const [name, setName] = useEditableField('name', fbUser?.displayName ?? '');
    const isAdmin = useIsAdmin();
    function onChangeNameMode(nameMode) {
        setNameMode(nameMode);
        if (nameMode == 'full') {
            setName(fbUser?.displayName ?? '');
        } else {
            setName('');
        }
    }

    return <View>
        <UtilityText weight='medium' label='Profile name'/>
        <Pad size={8} />
        <RadioGroup value={nameMode} onChange={onChangeNameMode}>
            <RadioOption radioKey='full' text={fbUser?.displayName ?? 'Unknown User'} />
            <RadioOption radioKey='custom' label='Add a custom name' />
        </RadioGroup>
        {nameMode == 'custom' && <Catcher><PseudonymEditor /></Catcher>}
        {fbUser?.photoURL &&
            <PadBox top={32}>
                <UtilityText weight='medium' label='Your profile photo'/>
                <Pad size={12} />
                <Catcher><ProfilePhotoEditor /></Catcher>
            </PadBox>
        }
    </View>
}

function makeHueForString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0; // Convert to a 32bit integer
    }
    return Math.abs(hash) % 360;
}

function ProfilePhotoEditor() {
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const fbUser = datastore.getFirebaseUser();
    const [photoUrl, setPhotoUrl] = useEditableField('photoUrl', fbUser?.photoURL ?? '');
    const defaultHue = makeHueForString(personaKey);
    const [hue, setHue] = useEditableField('hue', defaultHue);
    const [name, setName] = useEditableField('name', fbUser?.displayName ?? '');
    const [photoMode, setPhotoMode] = useEditableField('photoMode', 'photo');
    const isAdmin = useIsAdmin();
    const [showUrl, setShowUrl] = useState(false);


    function onSelectLetter() {
        setPhotoUrl(null); setHue(defaultHue); setPhotoMode('letter');
    }
    function onSelectPhoto() {
        setPhotoUrl(fbUser?.photoURL ?? ''); setHue(null); setPhotoMode('photo');
    }
    const isPhoto = photoMode == 'photo';

    return <View>
        {isAdmin && <Toggle spread label='Custom profile photo' value={showUrl} onChange={setShowUrl} />}
        {showUrl && <TextField multiline={false}
            testID='photo-url' ariaLabel='Photo URL' 
            value={photoUrl} placeholder='Photo URL' onChange={setPhotoUrl} 
        />}
        {isAdmin && <Pad size={12} />}
        {!showUrl &&<HorizBox>
            <FaceSelect testID='photo' selected={isPhoto} onSelect={onSelectPhoto}>
                <FaceImage type='large' photoUrl={photoUrl ?? fbUser?.photoURL ?? ''} />
            </FaceSelect>
            <Pad size={16} />
            <FaceSelect testID='letter' selected={!isPhoto} onSelect={onSelectLetter}>
                <LetterFace type='large' hue={defaultHue} name={name} />
            </FaceSelect>
        </HorizBox>}
    </View>
}

function PseudonymEditor() {
    const [name, setName] = useEditableField('name', '');
    const errors = useEditErrors();

    return <View>
        <TextField value={name} error={errors.hasError} multiline={false}
            onChange={setName} testID='pseudonym'
            placeholder='user123'/>
        <Pad size={12} />
        {errors.nameTaken ?
            <Banner color={colorTagEmphasized}>
                <UtilityText label='This custom name is already in use.'color={colorTextOnLightColorTag} />
            </Banner>
            : errors.nameInvalid ?
                <Banner color={colorTagEmphasized}>
                    <UtilityText label='Custom names can only include lowercase letters and numbers.' color={colorTextOnLightColorTag}/>
                </Banner>
                : errors.nameViolates ?
                    <Banner color={colorTagEmphasized}>
                        <UtilityText label='This custom name is incompatible with our Community Guidelines.' color={colorTextOnLightColorTag}/>
                    </Banner>            
                    : errors.nameLooksReal ?
                        <Banner color={colorTagEmphasized}>
                            <UtilityText label='Custom names are not allowed to look like real names.' color={colorTextOnLightColorTag}/>
                        </Banner>
                        :
                        <UtilityText type='small' color={colorTextSecondary} 
                            label='Custom names can only be changed once per week.'
                        />
        }
    </View>
}



async function checkPhotoAndNameAsync({datastore, updates}) {
    if (updates.nameMode == 'full') {
        return null;
    }
    const {name, nameMode} = updates;
    if (nameMode == 'custom') {
        if (!name || !name.match(/^[a-z0-9]+$/)) {
            return {nameInvalid: true};
        }
        const existingNameOwner = await datastore.getModulePublicAsync('profile', ['pseudonym', name]);
        if (existingNameOwner && existingNameOwner != datastore.getPersonaKey()) {
            return {nameTaken: true};
        }
        const violateResult = await datastore.callServerAsync('profile', 'checkName', {name});
        if (violateResult?.looksreal) {
            return {nameLooksReal: true};
        } else if (violateResult?.violates) {
            return {nameViolates: true};
        }
    }
    return null;
}

function previewPhotoAndName({updates}) {
    return {name: updates.name, photoUrl: updates.photoUrl, hue: updates.hue};
}

export function FirstLoginSetup({onFieldsChosen}) {
    const datastore = useDatastore();
    const fbUser = datastore.getFirebaseUser();
    const psmName = useSiloName();
    const showCollaborationNote = useShowCollaborationNote() || true;
    
    const defaultState = {name: fbUser?.displayName ?? '', photoUrl: fbUser?.photoURL ?? '', hue: makeHueForString(fbUser?.uid ?? ''    )};
    const [updates, setUpdates] = useState(defaultState);
    const [errors, setErrors] = useState({});
    const [inProgress, setInProgress] = useState(false);
    const [agreedConsent, setAgreedConsent] = useState(false);
    
    async function onFinish() {
        setInProgress(true);
        const errors = await checkPhotoAndNameAsync({datastore, updates});
        if (errors) {
            setErrors(errors);
            setInProgress(false);
            return;
        } else {
            setErrors({});
        }

        const preview = previewPhotoAndName({updates});

        onFieldsChosen({updates, preview});
    }

    return <View>
        <PadBox horiz={20} vert={20}>
            <Heading type="large" weight="medium" label="Set up your profile" />
            <Pad size={8} />
            <Paragraph type='large' color={colorTextSecondary} label='Choose a name and photo that appears when you post in discussions.' />
            <Pad size={40} />
            <WithEditableFields updates={updates} setUpdates={setUpdates} errors={errors}>
                <EditPhotoAndName />
            </WithEditableFields>
            <Pad size={32} />
            <ConsentAgreement value={agreedConsent} onChange={setAgreedConsent} />
            <Pad size={32} />
            <CTAButton disabled={inProgress || !agreedConsent} label={inProgress ? 'Please Wait...' : 'Finish'} onPress={onFinish} />
            <Pad size={16} />
            {psmName && showCollaborationNote && <Paragraph type='small' color={colorTextSecondary} label='This discussion space is hosted by New_ Public, in collaboration with {psmName}.' formatParams={{psmName}} /> }
            <Pad size={16} />
            <Paragraph type='small' color={colorTextSecondary} label='By participating in our beta, you may be invited to submit feedback on your experience using this product.' />
        </PadBox>
    </View>
}


function ConsentAgreement({value, onChange}) {
    const siloKey = useSiloKey();
    const legalDocLinks = useLegalDocLinks();
    const defaultCommunityGuidelinesLink = 'https://psi.zdf.de/pages/' + siloKey + '/community-guidelines.html';
    const defaultPrivacyNoticeLink = 'https://psi.zdf.de/pages/' + siloKey + '/privacy-notice.html';
    const defaultTermsOfServiceLink = 'https://psi.zdf.de/pages/' + siloKey + '/terms-of-service.html';
    const communityGuidelinesLink = legalDocLinks?.communityGuidelines ?? defaultCommunityGuidelinesLink;
    const privacyNoticeLink = legalDocLinks?.privacyNotice ?? defaultPrivacyNoticeLink;
    const termsOfServiceLink = legalDocLinks?.termsOfService ?? defaultTermsOfServiceLink;
    return <HorizBox center>
        <Checkbox value={value} onChange={onChange} ariaLabel='I agree to the Community Guidelines, Privacy Notice, and Terms of Service.' testID='consent-agreement' />
        <Pad size={12} />
        <RichText label='I agree to the [Community Guidelines]({communityGuidelinesLink}), [Privacy Notice]({privacyNoticeLink}), and [Terms of Service]({termsOfServiceLink}).'
            formatParams={{communityGuidelinesLink, privacyNoticeLink, termsOfServiceLink}}
            color={colorTextSecondary}
        />
    </HorizBox>
}

export function FirstLoginSetupTester() {
    const [updates, setUpdates] = useState(null);
    const [preview, setPreview] = useState(null);

    function onFieldsChosen({updates, preview}) {
        setUpdates(updates);
        setPreview(preview);
    }

    if (updates) {
        return <View>
            <Heading label='Fields chosen' />
            <Pad size={10} />
            <UtilityText text={JSON.stringify(updates)} />
            <Pad size={10} />
            <UtilityText label={JSON.stringify(preview)} />
        </View>
    } else {
        return <FirstLoginSetup onFieldsChosen={onFieldsChosen} />
    }
}
